import type { JsonValue } from '@bufbuild/protobuf';
import { Reading } from '../models/reading';
import { formatIsoSeconds } from './format';

const flatten = (value: JsonValue, prefix: string, out: Record<string, string>) => {
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    Object.entries(value).forEach(([k, v]) => flatten(v, prefix ? `${prefix}.${k}` : k, out));
  } else if (Array.isArray(value)) {
    out[prefix || 'value'] = JSON.stringify(value);
  } else {
    out[prefix || 'value'] = value === null ? '' : String(value);
  }
};

const escape = (cell: string) => (/[",\n\r]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell);

/** One row per reading with every payload field (nested keys joined with dots), in first-seen column order. */
export const readingsToCsv = (deviceUuid: string, readings: Reading[]): string => {
  const columns: string[] = [];
  const seen = new Set<string>();
  const rows = readings.map((r) => {
    const fields: Record<string, string> = {};
    flatten(r.payload, '', fields);
    Object.keys(fields).forEach((k) => {
      if (!seen.has(k)) {
        seen.add(k);
        columns.push(k);
      }
    });
    return { r, fields };
  });
  const fixed = ['time', 'device_uuid', 'topic'];
  const extra = columns.filter((c) => !fixed.includes(c));
  const lines = [[...fixed, ...extra].join(',')];
  rows.forEach(({ r, fields }) => {
    lines.push(
      [formatIsoSeconds(r.time), deviceUuid, r.topic, ...extra.map((c) => fields[c] ?? '')].map(escape).join(','),
    );
  });
  return lines.join('\n') + '\n';
};

export const csvFileName = (name: string, uuid: string, date: Date = new Date()) =>
  `${name.replace(/[^A-Za-z0-9_-]+/g, '') || 'device'}_${uuid.slice(0, 8)}_${date.toISOString().slice(0, 10)}.csv`;

export const downloadText = (content: string, fileName: string, type = 'text/csv;charset=utf-8') => {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
